import SalesReportService from "@/services/api/SalesReport";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import {
    createAsyncThunk,
    createSlice,
    isFulfilled,
    isRejected,
    isPending,
} from "@reduxjs/toolkit"

interface SalesReportState {
    salesByTeam: any[];
    salesByCustomer: any[];
    salesByProduct: any[];
    salesCoverage: any;
    salesCoverageByUserId: any;
    userSales: any[];
    userSalesLoading: boolean;
    salesBulkReport: any;
    salesBulkReportLoading: boolean;
    viewBulkReportSalesData: any;
    viewBulkReportLoading: boolean;
    exportLoading: boolean;
    isFetching: boolean;
    error: string | null;
}

const initialState: SalesReportState = {
    salesByTeam: [],
    salesByCustomer: [],
    salesByProduct: [],
    salesCoverage: null,
    salesCoverageByUserId: null,
    userSales: [],
    userSalesLoading: false,
    salesBulkReport: null,
    salesBulkReportLoading: false,
    viewBulkReportSalesData: null,
    viewBulkReportLoading: false,
    exportLoading: false,
    isFetching: false,
    error: null,
}

export const getSalesByTeam = createAsyncThunk(
    "report/salesByTeam",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.getSalesByTeam(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Sales by Team Report");
        }
    }
)

export const exportSalesByTeam = createAsyncThunk(
    "report/exportSalesByTeam",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportSalesByTeam(payload);
            return saveAsExcelFile(res, "SalesReport");
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to export Sales by Team Report");
        }
    }
)

export const getSalesByCustomer = createAsyncThunk(
    "report/salesByCustomer",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.getSalesByCustomer(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Sales by Customer Report");
        }
    }
)

export const exportSalesByCustomer = createAsyncThunk(
    "report/exportSalesByCustomer",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportSalesByCustomer(payload);
            return saveAsExcelFile(res, "SalesReport");
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to export Sales by Customer Report");
        }
    }
)

export const getSalesByProduct = createAsyncThunk(
    "report/salesByProduct",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.getSalesByProduct(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Sales by Product Report");
        }
    }
)

export const exportSalesByProduct = createAsyncThunk(
    "report/exportSalesByProduct",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportSalesByProduct(payload);
            return saveAsExcelFile(res, "SalesReport");
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to export Sales by Product Report");
        }
    }
)

export const getSalesCoverage = createAsyncThunk(
    "report/salesCoverage",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.getSalesCoverage(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Sales Coverage Report");
        }
    }
)

export const getSalesCoverageByUserId = createAsyncThunk(
    "report/salesCoverageByUserId",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.getSalesCoverageByUserId(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Sales Coverage Report by User ID");
        }
    }
)

export const exportSalesCoverageByUserId = createAsyncThunk(
    "report/exportSalesCoverageByUserId",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportSalesCoverageByUserId(payload);
            return saveAsExcelFile(res, "SalesReport");
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to export Sales Coverage Report by User ID");
        }
    }
)

export const exportSalesCoverage = createAsyncThunk(
    "report/exportSalesCoverage",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportSalesCoverage(payload);
            return saveAsExcelFile(res, "SalesReport");
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to export Sales Coverage Report");
        }
    }
)

export const getUserSales = createAsyncThunk(
    "report/userSales",
    async (payload: { month: number; year: number }, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.getUserSales(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch User Sales Report");
        }
    }
)

export const exportUserSales = createAsyncThunk(
    "report/exportUserSales",
    async (payload: { month: number; year: number }, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportUserSales(payload);
            return saveAsExcelFile(res, "UserSalesReport");
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to export User Sales Report");
        }
    }
)

export const uploadUserSales = createAsyncThunk(
    "report/uploadUserSales",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.uploadUserSales(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to upload User Sales Report");
        }
    }
)


export const getSalesBulkReport = createAsyncThunk(
    "report/getSalesBulkReport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.getSalesBulkReport(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch Sales Bulk Report");
        }
    }
)

export const viewBulkReportSales = createAsyncThunk(
    "report/viewBulkReportSales",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.viewBulkReportSales(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch View Bulk Report");
        }
    }
)

export const exportSalesBulkReport = createAsyncThunk(
    "report/exportSalesBulkReport",
    async (fileId: number, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportSalesBulkReport(fileId);
            return saveAsExcelFile(res, "SalesBulkReport");
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to export Sales Bulk Report");
        }
    }
)

export const downloadSalesBulkFile = createAsyncThunk(
    "report/downloadSalesBulkFile",
    async (fileId: number, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.downloadSalesBulkFile(fileId);
            return saveAsExcelFile(res, "OriginalSalesFile");
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to download original file");
        }
    }
)

export const exportViewBulkReportSales = createAsyncThunk(
    "report/exportViewBulkReportSales",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await SalesReportService.exportSalesviewBulkReport(payload);
            return saveAsExcelFile(res, "ViewBulkReport");
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to export View Bulk Report");
        }
    }
)

const salesReportSlice = createSlice({
    name: "salesReport",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSalesByTeam.fulfilled, (state, action) => {
                state.salesByTeam = action.payload || [];
            })
            .addCase(getSalesByCustomer.fulfilled, (state, action) => {
                state.salesByCustomer = action.payload || [];
            })
            .addCase(getSalesByProduct.fulfilled, (state, action) => {
                state.salesByProduct = action.payload || [];
            })
            .addCase(getSalesCoverage.fulfilled, (state, action) => {
                state.salesCoverage = action.payload || null;
            })
            .addCase(getSalesCoverageByUserId.fulfilled, (state, action) => {
                state.salesCoverageByUserId = action.payload || null;
            })
            .addCase(getUserSales.pending, (state) => {
                state.userSalesLoading = true;
            })
            .addCase(getUserSales.fulfilled, (state, action) => {
                state.userSales = action.payload || [];
                state.userSalesLoading = false;
            })
            .addCase(getUserSales.rejected, (state) => {
                state.userSalesLoading = false;
            })
            .addCase(getSalesBulkReport.pending, (state) => {
                state.salesBulkReportLoading = true;
            })
            .addCase(getSalesBulkReport.fulfilled, (state, action) => {
                state.salesBulkReport = action.payload || null;
                state.salesBulkReportLoading = false;
            })
            .addCase(getSalesBulkReport.rejected, (state) => {
                state.salesBulkReportLoading = false;
            })
            .addCase(viewBulkReportSales.pending, (state) => {
                state.viewBulkReportLoading = true;
            })
            .addCase(viewBulkReportSales.fulfilled, (state, action) => {
                state.viewBulkReportSalesData = action.payload || null;
                state.viewBulkReportLoading = false;
            })
            .addCase(viewBulkReportSales.rejected, (state) => {
                state.viewBulkReportLoading = false;
            })
            .addCase(exportSalesBulkReport.pending, (state) => {
                state.exportLoading = true;
            })
            .addCase(exportSalesBulkReport.fulfilled, (state) => {
                state.exportLoading = false;
            })
            .addCase(exportSalesBulkReport.rejected, (state) => {
                state.exportLoading = false;
            })
            .addCase(downloadSalesBulkFile.rejected, (state) => {
                state.exportLoading = false;
            })
            .addCase(exportViewBulkReportSales.pending, (state) => {
                state.exportLoading = true;
            })
            .addCase(exportViewBulkReportSales.fulfilled, (state) => {
                state.exportLoading = false;
            })
            .addCase(exportViewBulkReportSales.rejected, (state) => {
                state.exportLoading = false;
            })
        builder
            .addMatcher(isPending, (state) => {
                state.isFetching = true;
                state.error = null;
            })
            .addMatcher(isFulfilled, (state) => {
                state.isFetching = false;
            })
            .addMatcher(isRejected, (state, action: any) => {
                state.isFetching = false;
                state.error = action.payload || "Something went wrong";
            });
    }
})

export default salesReportSlice.reducer;